/*
 * Pure helpers for the AI search panel (assets/ai-search-panel.js), kept free of
 * the DOM so they can be unit-tested: label templating, collection matching,
 * typed-term suggestions, review excerpts and search-word highlighting.
 * Every list and threshold comes from the panel's JSON data (theme settings).
 */
(function () {
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // "See all [count] for [terms]" -> values filled in; unknown tags are kept.
  function fill(label, vars) {
    return String(label || '').replace(/\[(\w+)\]/g, function (m, k) { return k in vars ? vars[k] : m; });
  }

  function globToRegExp(p) {
    return new RegExp('^' + p.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*') + '$', 'i');
  }

  // The collection a search should open: an explicit "term | collection" link
  // first, else the non-excluded collection whose title contains every word of
  // the search (earliest match, then the largest collection). null = none.
  function collectionFor(term, data) {
    var t = String(term || '').toLowerCase().trim();
    var link = (data.links || []).filter(function (l) { return String(l.term).toLowerCase() === t; })[0];
    if (link) return link;
    var words = t.split(/\s+/).filter(Boolean);
    if (!words.length) return null;
    var exclude = (data.exclude || []).map(globToRegExp);
    return (data.collections || []).filter(function (c) {
      return c.n > 0 && !exclude.some(function (re) { return re.test(c.h); });
    }).map(function (c) {
      var title = String(c.t).toLowerCase();
      return { c: c, at: words.every(function (w) { return title.indexOf(w) >= 0; }) ? title.indexOf(words[0]) : -1 };
    }).filter(function (x) { return x.at >= 0; })
      .sort(function (a, b) { return a.at - b.at || b.c.n - a.c.n; })
      .map(function (x) { return x.c; })[0] || null;
  }

  // Terms worth offering while typing: the spelling fix first, then popular
  // searches and question terms that contain what was typed. Deduped, no echo.
  function suggestions(term, didYouMean, popular, questionTerms, limit) {
    var t = String(term || '').toLowerCase().trim(), seen = {}, out = [];
    [didYouMean].concat(popular || [], questionTerms || []).forEach(function (x) {
      var k = String(x || '').toLowerCase().trim();
      if (k && !seen[k] && k !== t && (k.indexOf(t) >= 0 || x === didYouMean)) { seen[k] = 1; out.push(x); }
    });
    return out.slice(0, limit);
  }

  function termWords(term, stop) {
    stop = stop || [];
    return String(term || '').toLowerCase().replace(/[^\p{L}\p{N}+\- ]/gu, ' ').split(/\s+/)
      .filter(function (w) { return w.length > 1 && stop.indexOf(w) < 0; });
  }

  // A word's stem, so "anxious" also finds "anxiety" and "dogs" finds "dog":
  // whole words of 4 letters lose one, longer words keep a bit over half
  // (at least 4). No word lists, so it works for any language or breed name.
  function stem(w) {
    var n = w.length;
    return n <= 3 ? w : w.slice(0, n === 4 ? 3 : Math.max(4, Math.ceil(n * 0.55)));
  }

  // Matches a whole word starting with the stem of `w`.
  function wordPattern(w) {
    return new RegExp('(^|[^\\p{L}\\p{N}])(' + stem(w).replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '[\\p{L}\\p{N}]*)', 'giu');
  }

  // A window of `max` characters around the first search word, with the search
  // words in <b>. Escaped HTML.
  function excerpt(text, term, max, stop) {
    text = String(text || '');
    var words = termWords(term, stop), low = text.toLowerCase();
    var first = words.reduce(function (m, w) { var i = low.indexOf(stem(w)); return i >= 0 && i < m ? i : m; }, Infinity);
    var start = first === Infinity || first < max / 2 ? 0 : text.indexOf(' ', Math.floor(first - max / 3)) + 1;
    var end = start + max < text.length ? text.lastIndexOf(' ', start + max) : text.length;
    if (end <= start) end = Math.min(text.length, start + max);
    var html = esc(text.slice(start, end).trim());
    words.forEach(function (w) { html = html.replace(wordPattern(esc(w)), '$1<b>$2</b>'); });
    return (start > 0 ? '…' : '') + html + (end < text.length ? '…' : '');
  }

  // The typed text in <b> inside a suggestion. Escaped HTML.
  function highlight(text, term) {
    var t = String(term || '').trim(), i = String(text).toLowerCase().indexOf(t.toLowerCase());
    return i < 0 || !t ? esc(text) : esc(text.slice(0, i)) + '<b>' + esc(text.slice(i, i + t.length)) + '</b>' + esc(text.slice(i + t.length));
  }

  var api = { esc: esc, stem: stem, fill: fill, collectionFor: collectionFor, suggestions: suggestions, termWords: termWords, excerpt: excerpt, highlight: highlight };
  if (typeof window !== 'undefined') window.AISearchPanelUtil = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
